import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Globe, Menu, X, Home, ShoppingBag, ArrowUpRight } from 'lucide-react';
import { categories } from '@/mocks/data';

const languages = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'da', label: 'Dansk' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setLangOpen(false);
  };

  const navLinks = [
    { label: t('nav.home'), href: '/', external: false },
    { label: t('nav.spreadsheet'), href: 'https://kaspreadsheet.com', external: true },
    { label: t('nav.categories'), href: '#categories', external: false },
    { label: t('nav.guides'), href: '#guides', external: false },
    { label: t('nav.review'), href: '#review', external: false },
    { label: t('nav.shipping'), href: '#guides', external: false },
  ];

  const linkColor = scrolled
    ? 'text-foreground-700 hover:text-primary-600'
    : 'text-background-50/90 hover:text-background-50';
  const logoColor = scrolled ? 'text-foreground-950' : 'text-background-50';
  const globeColor = scrolled ? 'text-foreground-700' : 'text-background-50';
  const menuColor = scrolled ? 'text-foreground-700' : 'text-background-50';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-gradient-to-b from-foreground-950/50 to-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-8 xl:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a
            href="/"
            className={`font-heading text-lg md:text-xl font-bold tracking-tight whitespace-nowrap transition-colors ${logoColor}`}
          >
            {t('site.name')}
          </a>

          <div className="hidden lg:flex items-center gap-0.5">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target={link.external ? '_blank' : undefined}
                rel={link.external ? 'noopener nofollow' : undefined}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${linkColor}`}
              >
                {link.label}
              </a>
            ))}

            <a
              href="#guides"
              className="ml-2 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md transition-colors whitespace-nowrap"
            >
              <ShoppingBag className="w-4 h-4" />
              {t('nav.shoppingGuide')}
            </a>

            <div className="relative ml-3">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors ${globeColor}`}
              >
                <Globe className="w-4 h-4" />
                <span className="uppercase text-xs">{i18n.language}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-background-50 rounded-lg border border-background-200/60 shadow-lg py-1 animate-fade-in">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        i18n.language === lang.code
                          ? 'text-primary-600 bg-primary-50/50'
                          : 'text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className={`flex items-center gap-1 px-2 py-1.5 text-sm ${globeColor}`}
              >
                <Globe className="w-4 h-4" />
                <span className="uppercase text-xs">{i18n.language}</span>
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-1 w-36 bg-background-50 rounded-lg border border-background-200/60 shadow-lg py-1 animate-fade-in z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        i18n.language === lang.code
                          ? 'text-primary-600 bg-primary-50/50'
                          : 'text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`p-2 ${menuColor}`}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200/60 animate-fade-in max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target={link.external ? '_blank' : undefined}
                rel={link.external ? 'noopener nofollow' : undefined}
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-between px-3 py-2.5 text-sm font-medium text-foreground-800 hover:text-primary-600 rounded-md transition-colors"
              >
                {link.label}
                {link.external && <ArrowUpRight className="w-4 h-4 text-foreground-400" />}
              </a>
            ))}

            <a
              href="#guides"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-1.5 px-3 py-3 mt-1 text-sm font-semibold bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md transition-colors"
            >
              <ShoppingBag className="w-4 h-4" />
              {t('nav.shoppingGuide')}
            </a>

            <div className="pt-3 mt-2 border-t border-background-200/60">
              <button
                onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
                className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold text-foreground-900 rounded-md"
              >
                <span className="flex items-center gap-2">
                  <Home className="w-4 h-4 text-primary-600" />
                  {t('nav.categories')}
                </span>
                <ChevronDown className={`w-4 h-4 text-foreground-500 transition-transform ${mobileCategoriesOpen ? 'rotate-180' : ''}`} />
              </button>

              {mobileCategoriesOpen && (
                <div className="grid grid-cols-2 gap-1.5 mt-2 px-1 pb-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-background-100 hover:bg-primary-50/60 border border-background-200/50 transition-colors"
                    >
                      <img
                        src={cat.image}
                        alt={cat.name}
                        className="w-8 h-8 rounded-md object-cover flex-shrink-0"
                        loading="lazy"
                      />
                      <span className="text-xs font-medium text-foreground-700 leading-tight">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}